import { useState, useEffect } from "react";
import type {
  UserWithRelations,
  LoginCredentials,
  CreateUserData,
} from "@/lib/auth/types";
import { isAdminUser } from "@/utils/admin-protection";
import { useStore } from "@/store/useStore";

interface UseAuthActionsReturn {
  user: UserWithRelations | null;
  loading: boolean;
  error: string | null;
  accessToken: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: CreateUserData) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  clearError: () => void;
  setUser: (user: UserWithRelations | null) => void;
  setAccessToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export function useAuthActions(): UseAuthActionsReturn {
  const [user, setUser] = useState<UserWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Initialize access token from localStorage on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedToken = localStorage.getItem("accessToken");
      if (storedToken) {
        setAccessToken(storedToken);
      }
    }
  }, []);

  // Persist access token to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (accessToken) {
        localStorage.setItem("accessToken", accessToken);
      } else {
        localStorage.removeItem("accessToken");
      }
    }
  }, [accessToken]);

  const login = async (credentials: LoginCredentials) => {
    // console.log('Frontend: Login function called with credentials:', credentials); // Debug: Function called
    try {
      setLoading(true);
      setError(null);

      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(credentials),
      });

      const data = await response.json();
      // console.log('Frontend: Login response data:', data); // Debug: Response data

      // if (!response.ok) {
      //   throw new Error(data.error || "Login failed");
      // }

      if (!response.ok) {
        // Properly extract the error message from the API response
        const errorMessage = data.error?.message || data.message || "Login failed";
        const error = new Error(errorMessage);
        // Attach the status code for better error handling
        (error as any).status = response.status;
        (error as any).code = data.error?.code;
        throw error;
      }

      setUser(data.user);
      setAccessToken(data.token);

      // Debug user structure
      console.log('Frontend: User object structure:', data.user); // Debug: User object
      console.log('Frontend: User roles:', data.user.roles); // Debug: User roles

      // Force clear initialization flag for debugging
      if (typeof window !== 'undefined') {
        localStorage.removeItem("hasInitializedClients");
        console.log('Frontend: Cleared hasInitializedClients flag'); // Debug: Clear flag
      }

      // Initialize client selection for non-admin users
      console.log('Frontend: isAdminUser check:', isAdminUser(data.user)); // Debug: Admin check
      console.log('Frontend: hasInitializedClients check:', localStorage.getItem("hasInitializedClients")); // Debug: Init check
      
      // Always initialize client names for non-admin users, or if not already initialized
      // TEMPORARY: Force initialization for debugging
      const forceInit = true; // Set to false to restore normal behavior
      const isAdmin = isAdminUser(data.user);
      const hasInitialized = localStorage.getItem("hasInitializedClients");
      const condition1 = !isAdmin || forceInit;
      const condition2 = !hasInitialized;
      const finalCondition = condition1 && condition2;
      
      console.log('Frontend: Debug conditions:', {
        isAdmin,
        forceInit,
        hasInitialized,
        condition1,
        condition2,
        finalCondition
      }); // Debug: All conditions
      
      if (finalCondition) {
        console.log('Frontend: Running client initialization...'); // Debug: Init running
        const clientAccess = data.user.account_access || [];
        console.log('Frontend: clientAccess from backend:', clientAccess); // Debug: Backend data
        const clientIds = clientAccess.map((a: { account_uid: string }) => a.account_uid);
        const clientNames = clientAccess.reduce((acc: Record<string, string>, a: { account_uid: string, account_name?: string }) => ({
          ...acc,
          [a.account_uid]: a.account_name || `Client ${a.account_uid}`
        }), {});
        console.log('Frontend: clientNames mapping:', clientNames); // Debug: Frontend mapping
        console.log('Frontend: clientIds:', clientIds); // Debug: Client IDs

        const { setSelectedClientIds, setSelectedClientNames, setHasInitializedClients } = useStore.getState();
        setSelectedClientIds(clientIds);
        setSelectedClientNames(clientNames);
        setHasInitializedClients(true);
        console.log('Frontend: Client initialization complete'); // Debug: Init complete
      } else {
        console.log('Frontend: Skipping client initialization'); // Debug: Skip reason
      }

      // Return the user object for immediate redirect logic
      return data.user;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Login failed";
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (data: CreateUserData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || "Registration failed");
      }

      setUser(responseData.user);
      setAccessToken(responseData.token);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Registration failed";
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Clear all user-related state from store
      const store = useStore.getState();
      store.clearUser(); // This clears user and client selection
      store.clearClientSelection(); // Additional cleanup for client selection
      
      // Clear additional localStorage items that might contain user-specific data
      if (typeof window !== 'undefined') {
        localStorage.removeItem('persistedTotalCount');
        // localStorage.removeItem('app-storage');
        // Client state cleanup is now handled by clearUser() in the store
        // localStorage.removeItem('app-storage'); // Removed - persist middleware manages this
      }

      if (user) {
        await fetch("/api/auth/logout", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ userId: user.id }),
        });
      }

      setUser(null);
      setAccessToken(null);
    } catch (error) {
      console.error("Logout error:", error);
      setUser(null);
      setAccessToken(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setAccessToken(data.token);
        return true;
      }
      setUser(null);
      setAccessToken(null);
      const body = await response.json().catch(() => ({}));
      const msg = body?.error ?? response.statusText;
      console.warn("[Auth] Refresh token 401:", msg);
      return false;
    } catch (error) {
      console.error("Token refresh failed:", error);
      setUser(null);
      setAccessToken(null);
      return false;
    }
  };

  const clearError = () => {
    setError(null);
  };

  return {
    user,
    loading,
    error,
    accessToken,
    login,
    register,
    logout,
    refreshToken,
    clearError,
    setUser,
    setAccessToken,
    setLoading,
    setError,
  };
}
